/**
 * The small subset of Markdown the analyst actually emits.
 *
 * Headings, bullets, numbered lists, tables and bold. Deliberately not a
 * Markdown library: this builds React elements rather than accepting arbitrary
 * HTML from model output, so nothing the model writes can become markup.
 *
 * Anything it does not recognise falls through as text rather than being
 * dropped, which keeps an unrendered reply readable rather than empty.
 */
import type { ReactNode } from "react";
import { humanise } from "../services/chartOptions";
import { readableText } from "../services/displayText";

/** Matches a bold run, keeping the content as a capture group. */
const BOLD = /\*\*([^*]+)\*\*/g;

/** Renders the inline bold in one line of text. */
function inline(text: string, key: string): ReactNode[] {
  // A qualified member quoted in the sentence is the same leak as a column
  // header, whether bare, bold or in backticks. A backtick span that is not a
  // member stays.
  const readable = readableText(
    text.replace(/`([^`]+)`/g, (full, name: string) =>
      TECHNICAL_NAME.test(name.trim()) ? humanise(name.trim()) : full,
    ),
  );
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let index = 0;
  for (const match of readable.matchAll(BOLD)) {
    const at = match.index ?? 0;
    if (at > cursor) nodes.push(readable.slice(cursor, at));
    nodes.push(<strong key={`${key}-b${index}`}>{match[1]}</strong>);
    cursor = at + match[0].length;
    index += 1;
  }
  if (cursor < readable.length) nodes.push(readable.slice(cursor));
  return nodes;
}

const HEADING = /^(#{1,4})\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
/**
 * A numbered item.
 *
 * Needed because the agent numbers things far more often than it bullets them --
 * a proposed document structure arrives as "1. Headline ... 2. KPI ..." -- and
 * without this each item became its own paragraph, losing the numbering the
 * reader is being asked to approve.
 */
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const TABLE_SEPARATOR_CELL = /^:?-{3,}:?$/;

/**
 * A qualified member (`utilization_cognos.customer_name`) or a physical column
 * (`customer_name`). Titles a person already wrote, such as "Customer name",
 * do not match.
 */
const TECHNICAL_NAME =
  /^(?:[A-Za-z][\w]*(?:\.[A-Za-z][\w]*)+|[a-z][a-z0-9]*(?:_[a-z0-9]+)+)$/;

/**
 * The business title for a column header.
 *
 * Query results arrive keyed by the physical member. A reader wants "Customer
 * name", and the member name stays available to the query that produced the
 * column. A header that is already a title is left as written.
 */
function columnTitle(cell: string): string {
  const plain = cell
    .trim()
    .replace(/^`+|`+$/g, "")
    .replace(/^\*\*|\*\*$/g, "")
    .trim();
  return TECHNICAL_NAME.test(plain) ? humanise(plain) : cell;
}

/** Cells in one GitHub-style pipe-table row. */
function tableCells(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.includes("|")) return null;
  const withoutEdges = trimmed.replace(/^\|/, "").replace(/\|$/, "");
  const cells = withoutEdges.split("|").map((cell) => cell.trim());
  return cells.length >= 2 ? cells : null;
}

/** The second line that distinguishes a table from ordinary text with pipes. */
function isTableSeparator(line: string, columns: number): boolean {
  const cells = tableCells(line);
  return Boolean(
    cells
    && cells.length === columns
    && cells.every((cell) => TABLE_SEPARATOR_CELL.test(cell)),
  );
}

/**
 * Recovers the other common analyst shape: a header followed directly by data.
 *
 * Models sometimes omit the Markdown separator row entirely. Requiring each
 * line to have both edge pipes, the same column count, and at least one body row
 * keeps ordinary prose such as `A | B` from being promoted into a table.
 */
function separatorlessTable(
  lines: string[],
  start: number,
): { header: string[]; rows: string[][]; end: number } | null {
  const first = (lines[start] ?? "").trim();
  if (!first.startsWith("|") || !first.endsWith("|")) return null;
  const header = tableCells(first);
  if (!header) return null;

  const rows: string[][] = [];
  let position = start + 1;
  while (position < lines.length) {
    const line = (lines[position] ?? "").trim();
    if (!line.startsWith("|") || !line.endsWith("|")) break;
    const row = tableCells(line);
    if (!row || row.length !== header.length) break;
    rows.push(row);
    position += 1;
  }
  return rows.length
    ? { header, rows, end: position - 1 }
    : null;
}

/**
 * Recovers the compact table shape the agent sometimes emits.
 *
 * Instead of newline-separated rows it returns:
 * `| A | B | |---|---| | one | two |`
 * The empty token between two pipe characters is the only row boundary. This
 * is not valid Markdown, but it is unambiguous once the second group is a table
 * separator, so handling it here is safer than asking CSS to make raw pipes
 * look tabular.
 */
function compactTable(
  line: string,
): { header: string[]; rows: string[][] } | null {
  if (!line.trimStart().startsWith("|")) return null;
  // Ignore empty tokens entirely. Some model responses use `| |` between rows,
  // while others concatenate the closing and opening pipes without a usable
  // boundary. The consecutive dash cells still tell us both where the header
  // ends and how wide every row is.
  const cells = line
    .split("|")
    .map((token) => token.trim())
    .filter(Boolean);
  const separatorAt = cells.findIndex((cell) =>
    TABLE_SEPARATOR_CELL.test(cell),
  );
  if (separatorAt < 2) return null;
  let separatorEnd = separatorAt;
  while (
    separatorEnd < cells.length
    && TABLE_SEPARATOR_CELL.test(cells[separatorEnd] ?? "")
  ) {
    separatorEnd += 1;
  }
  const header = cells.slice(0, separatorAt);
  const separator = cells.slice(separatorAt, separatorEnd);
  if (
    header.length < 2
    || separator.length !== header.length
  ) {
    return null;
  }
  const body = cells.slice(separatorEnd);
  const rows: string[][] = [];
  for (let at = 0; at < body.length; at += header.length) {
    rows.push(
      header.map((_cell, index) => body[at + index] ?? ""),
    );
  }
  return {
    header,
    rows,
  };
}

/**
 * A model may put prose, a compact table and more prose on one physical line.
 * The first and last pipes delimit the candidate table; content outside them
 * remains ordinary prose. Validation still happens in compactTable, so a
 * sentence containing `A | B` is not promoted into a table.
 */
function embeddedCompactTable(line: string): {
  before: string;
  table: { header: string[]; rows: string[][] };
  after: string;
} | null {
  const firstPipe = line.indexOf("|");
  const lastPipe = line.lastIndexOf("|");
  if (firstPipe < 0 || lastPipe <= firstPipe) return null;
  const table = compactTable(line.slice(firstPipe, lastPipe + 1));
  if (!table) return null;
  return {
    before: line.slice(0, firstPipe).trim(),
    table,
    after: line.slice(lastPipe + 1).trim(),
  };
}

function paragraphBlock(text: string, key: string): ReactNode {
  return <p key={key}>{inline(text, key)}</p>;
}

function tableBlock(
  header: string[],
  rows: string[][],
  key: string,
): ReactNode {
  return (
    <div className="markdown-table-wrap" key={key}>
      <table className="markdown-table">
        <thead>
          <tr>
            {header.map((cell, index) => (
              <th key={index}>{inline(columnTitle(cell), `${key}-h${index}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {header.map((_heading, columnIndex) => (
                <td key={columnIndex}>
                  {inline(row[columnIndex] ?? "", `${key}-${rowIndex}-${columnIndex}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Markdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  // The lines gathered so far, and which kind of block they will become. A
  // blank line, or a line of a different kind, closes the block.
  let bucket: string[] = [];
  let kind: "list" | "numbered" | "paragraph" | null = null;

  function flush() {
    if (!bucket.length) return;
    const key = `b${blocks.length}`;
    if (kind === "list" || kind === "numbered") {
      const items = bucket.map((item, position) => (
        <li key={position}>{inline(item, `${key}-${position}`)}</li>
      ));
      blocks.push(
        kind === "numbered" ? (
          <ol key={key}>{items}</ol>
        ) : (
          <ul key={key}>{items}</ul>
        ),
      );
    } else {
      // Joined with a space: a hard-wrapped paragraph is one paragraph, which is
      // how the model wraps its prose.
      blocks.push(<p key={key}>{inline(bucket.join(" "), key)}</p>);
    }
    bucket = [];
    kind = null;
  }

  for (let position = 0; position < lines.length; position += 1) {
    const line = lines[position] ?? "";
    if (!line.trim()) {
      flush();
      continue;
    }

    const embedded = embeddedCompactTable(line);
    if (embedded) {
      flush();
      if (embedded.before) {
        blocks.push(
          paragraphBlock(embedded.before, `b${blocks.length}`),
        );
      }
      blocks.push(
        tableBlock(
          embedded.table.header,
          embedded.table.rows,
          `b${blocks.length}`,
        ),
      );
      if (embedded.after) {
        blocks.push(
          paragraphBlock(embedded.after, `b${blocks.length}`),
        );
      }
      continue;
    }

    // A header followed by a separator is unambiguously a table. Detect it
    // before paragraph bucketing, otherwise each row is deliberately joined
    // with spaces and the user sees raw pipes on one long line.
    const header = tableCells(line);
    const separator = lines[position + 1] ?? "";
    if (header && isTableSeparator(separator, header.length)) {
      flush();
      const rows: string[][] = [];
      position += 2;
      while (position < lines.length) {
        const row = tableCells(lines[position] ?? "");
        if (!row) break;
        rows.push(row);
        position += 1;
      }
      // The outer loop increments once more. Step back so the first non-table
      // line is still processed (or the final row remains the final line).
      position -= 1;
      blocks.push(tableBlock(header, rows, `b${blocks.length}`));
      continue;
    }
    const inferredTable = separatorlessTable(lines, position);
    if (inferredTable) {
      flush();
      blocks.push(
        tableBlock(
          inferredTable.header,
          inferredTable.rows,
          `b${blocks.length}`,
        ),
      );
      position = inferredTable.end;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      const key = `b${blocks.length}`;
      // One visual level regardless of depth. The analyst has no document
      // outline to reflect, and an h2 inside a chat bubble is a section title
      // whatever number of hashes it arrived with.
      blocks.push(
        <p className="markdown-heading" key={key}>
          {inline(heading[2] ?? "", key)}
        </p>,
      );
      continue;
    }
    const bullet = BULLET.exec(line);
    if (bullet) {
      if (kind !== "list") flush();
      kind = "list";
      bucket.push(bullet[1] ?? "");
      continue;
    }
    const numbered = NUMBERED.exec(line);
    if (numbered) {
      if (kind !== "numbered") flush();
      kind = "numbered";
      bucket.push(numbered[1] ?? "");
      continue;
    }
    if (kind !== "paragraph") flush();
    kind = "paragraph";
    bucket.push(line.trim());
  }
  flush();

  return <div className="markdown">{blocks}</div>;
}

export default Markdown;
